import React, { useContext, useEffect, useMemo, useState } from 'react';
import './styles.scss';
import Dropdown from '../../components/Dropdown';
import { SelectMarketProps } from './interfaces';
import { ScreenEnum } from '../../enums';
import Progress from '../../components/Progress';
import { useTranslation } from 'react-i18next';
import AppStateContext from '../../AppStateProvider/context';
import Input from '../../components/Input';
import WarningOverlay from '../../components/WarningOverlay';
import { isValidUrl } from '../../utils/isValidUrl';
import { audi, bentley, bentley2, cupra, vw } from '../../assets/Icons';

export default function SelectMarket(props: SelectMarketProps) {
	const { t } = useTranslation();
	const { state, dispatch } = useContext(AppStateContext);

	const [clicks, setClicks] = useState<number>(0);
	const [lastClick, setLastClick] = useState<number | null>(null);
	const [expertMode, setExpertMode] = useState<boolean>(
		(localStorage.getItem('use_qs') || 'false') === 'true'
	);
	const [showErrorOverlay, setShowErrorOverlay] = useState<boolean>(true);

	const [brand, setBrand] = useState(
		state.dataJson.brands?.find(
			(b: any) => b.name === (state.brand || 'audi')
		)
	);

	useEffect(() => {
		setBrand(
			state.dataJson.brands?.find((b: any) => b.name === state.brand)
		);
	}, [state.brand]);

	useEffect(() => {
		if (state.brand != null) localStorage.setItem('brand', state.brand);
	}, [state.brand]);

	useEffect(() => {
		if (state.market?.name)
			localStorage.setItem('market', state.market.name);
	}, [state.market]);

	useEffect(() => {
		if (expertMode) {
			localStorage.setItem('use_qs', 'true');
			const qs = state.market?.qsUrl;
			if (qs && qs !== state.url) {
				dispatch({ type: 'SET_URL', value: qs });
			}
			// set endpoints to undefined for expert mode
			dispatch({ type: 'SET_ENDPOINTS', value: undefined as any });
		} else {
			localStorage.setItem('use_qs', 'false');
			if (state.market?.urls) {
				dispatch({ type: 'SET_ENDPOINTS', value: state.market.urls });
			}
		}
	}, [expertMode]);

	const customMarktOptions = (option: any) => ({
		...option,
		label: !option.label.includes(' ') ? (
			option.label
		) : (
			<>
				{option.label.slice(0, option.label.indexOf(' ') + 1)}
				<span className='small'>
					<br />
					{option.label.slice(option.label.indexOf(' ') + 1)}
				</span>
			</>
		),
	});

	function expertModeToggle() {
		if (lastClick === null || Date.now() - lastClick > 1000) {
			setLastClick(Date.now());
			setClicks(1);
		} else {
			if (clicks === 12) setExpertMode(v => !v);
			setLastClick(Date.now());
			setClicks(prev => prev + 1);
		}
	}

	const endpointsValid = useMemo(() => {
		const ep = state.endpoints || {};
		return Boolean(ep.pairing && ep.delivery && ep.vitals);
	}, [state.endpoints]);

	const isConfirmDisabled = expertMode
		? !(state.url && isValidUrl(state.url))
		: !(state.brand && state.market && endpointsValid);

	return (
		<>
			<div className='top'>
				<div className='select-brand'>
					{props.backBtn(
						state.apk ? ScreenEnum.setSecond : ScreenEnum.welcome
					)}

					<h1 onClick={expertModeToggle}>
						{t('SelectMarketHeadline')}
					</h1>

					{state.errors.validationError ? (
						<p className='p1 error-message'>
							{t('SelectMarketErrorMessage')}
						</p>
					) : (
						<p className='p1'>{t('SelectMarketText')}</p>
					)}

					<Progress
						screen={ScreenEnum.selectMarket}
						setScreen={props.setScreen}
					/>

					<div className='brands'>
						{state.dataJson.brands.map((b: any, index: number) => (
							<button
								key={`brand-${index}`}
								className={`brand${state.brand === b.name ? ' active' : ''}`}
								disabled={b.disabled}
								onClick={() => {
									dispatch({
										type: 'SET_BRAND',
										value: b.name,
									});
									const nextMarket =
										b.markets?.find(
											(m: any) => m.name === 'DEU'
										) ||
										b.markets?.[0] ||
										null;
									dispatch({
										type: 'SET_MARKET',
										value: nextMarket,
									});

									if (!expertMode && nextMarket?.urls) {
										dispatch({
											type: 'SET_ENDPOINTS',
											value: nextMarket.urls,
										});
									}
									if (expertMode && nextMarket?.qsUrl) {
										dispatch({
											type: 'SET_URL',
											value: nextMarket.qsUrl,
										});
									}
								}}
							>
								{b.name === 'bentley' &&
								state.brand === 'bentley'
									? bentley2
									: b.name === 'bentley'
										? bentley
										: b.name === 'audi'
											? audi
											: b.name === 'vw'
												? vw
												: cupra}
							</button>
						))}
					</div>
				</div>

				<div className='select-market'>
					<form autoComplete='off'>
						{!expertMode && (
							<label htmlFor='market'>
								<h4>{t('SelectMarketLabelMarket')}</h4>
								<Dropdown
									list={brand?.markets || []}
									set={(marketName: any) => {
										const selected = (
											brand?.markets || []
										).find(
											(m: any) => m.name === marketName
										);
										dispatch({
											type: 'SET_MARKET',
											value: selected,
										});
										if (selected?.urls)
											dispatch({
												type: 'SET_ENDPOINTS',
												value: selected.urls,
											});
									}}
									defaultValue={state.market?.name || 'DEU'}
									customOptions={customMarktOptions}
								/>
							</label>
						)}

						{expertMode && (
							<Input
								labelId='label-url'
								labelHeadline={t('SelectMarketLabelURL')}
								errorMessage={
									state.errors.urlError ||
									(state.url && !isValidUrl(state.url))
										? t('SelectMarketErrorURL')
										: undefined
								}
								type='text'
								id='url'
								value={state.url || ''}
								placeholder={t('SelectMarketPlaceholderURL')}
								onInput={e => {
									const v = (e.target as HTMLInputElement)
										.value;
									dispatch({ type: 'SET_URL', value: v });
								}}
								required
								inputMode='url'
							/>
						)}
					</form>

					{showErrorOverlay &&
						state.errors.validationError +
							state.errors.urlError +
							state.errors.passwordError >
							5 && (
							<WarningOverlay
								headline={'ValidationFailedHeadline'}
								texts={['ValidationFailedText']}
								btnFunction={() => setShowErrorOverlay(false)}
							/>
						)}
				</div>
			</div>

			<div className='buttons'>
				<button
					className='white'
					disabled={isConfirmDisabled}
					onClick={() => {
						if (state.brand)
							localStorage.setItem('brand', state.brand);
						if (state.market?.name)
							localStorage.setItem('market', state.market.name);

						if (!expertMode) {
							const ep = state.endpoints || {};
							if (ep.pairing)
								localStorage.setItem('url_pairing', ep.pairing);
							if (ep.delivery)
								localStorage.setItem(
									'url_delivery',
									ep.delivery
								);
							if (ep.vitals)
								localStorage.setItem('url_vitals', ep.vitals);
							localStorage.removeItem('qs_url');
							localStorage.setItem('use_qs', 'false');
						} else {
							if (state.url)
								localStorage.setItem('qs_url', state.url);
							localStorage.removeItem('url_pairing');
							localStorage.removeItem('url_delivery');
							localStorage.removeItem('url_vitals');
							localStorage.setItem('use_qs', 'true');
						}

						props.setScreen(ScreenEnum.pairDevice);
					}}
				>
					{t('ButtonConfirm')}
				</button>
				{props.cancelBtn}
			</div>
		</>
	);
}
